import { useMemo } from "react";
import { Item } from "../../../../data-model/documentModalWithSideBav";
import {
  getMenuOptionsList,
  INGESTING_IN_PATIENT_GRAPH_LABEL,
  MENU_OPTIONS,
  MenuOption,
} from "../constants/menuOptions";

/**
 * Custom hook that generates the menu options based on available items.
 *
 * @param items - Array of document items to check for guidelines availability
 * @param pmgAlreadyIngested - When true, hide "Ingest in Patient Graph"
 * @param isIngesting - When true, show "Ingesting..." on the ingest option
 * @returns Object containing the menu options and whether guidelines are available
 */
export const useMenuOptions = (
  items: Item[],
  pmgAlreadyIngested: boolean = false,
  isIngesting: boolean = false,
): {
  options: MenuOption[];
  shouldShowGuidelines: boolean;
} => {
  const shouldShowGuidelines = useMemo(() => {
    if (!items || items.length === 0) {
      return false;
    }

    // Check if there's at least one document across all items
    return items.some((item) => item?.documents && item.documents.length > 0);
  }, [items]);

  const options = useMemo(() => {
    const list = getMenuOptionsList(shouldShowGuidelines, !pmgAlreadyIngested);
    if (!isIngesting) {
      return list;
    }
    return list.map((option) =>
      option.id === MENU_OPTIONS.INGEST_IN_PATIENT_GRAPH
        ? { ...option, text: INGESTING_IN_PATIENT_GRAPH_LABEL }
        : option,
    );
  }, [shouldShowGuidelines, pmgAlreadyIngested, isIngesting]);

  return {
    options,
    shouldShowGuidelines,
  };
};
